/**
 * Subscription Types
 * 
 * Types for the subscription management system that controls
 * access to SUBSCRIBER-visibility portfolios and baskets.
 */

// Visibility type - shared across portfolios and baskets
export type Visibility = 'PUBLIC' | 'PRIVATE' | 'SUBSCRIBER';

export const VISIBILITY_OPTIONS: { value: Visibility; label: string; description: string }[] = [
  { value: 'PRIVATE', label: 'Private', description: 'Only you can see this' },
  { value: 'PUBLIC', label: 'Public', description: 'Everyone can see this' },
  { value: 'SUBSCRIBER', label: 'Subscriber Only', description: 'Only subscribed users can see this' },
];

/**
 * Subscription settings controlling access to provider content.
 * 
 * Access control logic:
 * - [] (empty array): Access to ALL subscriber-visibility items
 * - [1, 2]: Access to SPECIFIC items with those IDs only
 * - [-1]: NO subscriber access (public content only)
 */
export interface SubscriptionSettings {
  portfolios: number[];
  baskets: number[];
  isSubscribedExternallyToProvider?: boolean;
}

/**
 * Defensive normalization for subscription settings coming off the wire:
 * non-array portfolios/baskets collapse to [] (full access semantics stay
 * intact because [] already means "all").
 */
export function normalizeSubscriptionSettings(
  settings: SubscriptionSettings | null | undefined
): SubscriptionSettings {
  return {
    portfolios: Array.isArray(settings?.portfolios) ? settings.portfolios : [],
    baskets: Array.isArray(settings?.baskets) ? settings.baskets : [],
    isSubscribedExternallyToProvider: settings?.isSubscribedExternallyToProvider,
  };
}

/**
 * Core subscription entity representing a user's subscription to a provider.
 */
export interface Subscription {
  id: number;
  providerId: number;
  subscriptionUserId: string;
  subscriptionSettings: SubscriptionSettings;
  createdAtUtc: string;
  createdBy: string;
  updatedAtUtc: string;
  updatedBy: string;
}

/**
 * Provider info embedded in subscription responses.
 */
export interface SubscriptionProvider {
  id: number;
  name: string;
  logoUrl: string | null;
}

/**
 * Subscriber user info embedded in subscription responses.
 */
export interface SubscriptionUser {
  id: string;
  email: string;
  name: string | null;
}

/**
 * Subscription with related provider and user details.
 * Returned by GET endpoints.
 */
export interface SubscriptionWithRelations extends Subscription {
  provider: SubscriptionProvider;
  subscriptionUser: SubscriptionUser;
}

// ============================================================
// API Request/Response Types
// ============================================================

/**
 * Single subscription item in a batch create request.
 */
export interface CreateSubscriptionItem {
  userId: string;
  subscriptionSettings?: SubscriptionSettings;
}

/**
 * Request body for POST /v3/providers/{providerId}/subscriptions
 */
export interface CreateSubscriptionsRequest {
  subscriptions: CreateSubscriptionItem[];
}

/**
 * Result for a single subscription creation attempt.
 */
export interface CreateSubscriptionResult {
  userId: string;
  success: boolean;
  subscription?: Subscription;
  error?: string;
}

/**
 * Result for a single investor-context subscription creation attempt.
 */
export interface CreateInvestorSubscriptionResult {
  providerId: number;
  success: boolean;
  subscription?: Subscription;
  error?: string;
}

/**
 * Response from POST /v3/providers/{providerId}/subscriptions
 */
export interface CreateSubscriptionsResponse {
  message: string;
  results: CreateSubscriptionResult[];
}

/**
 * Request body for POST /v3/investors/{userId}/subscriptions
 */
export interface CreateInvestorSubscriptionItem {
  providerId: number;
  subscriptionSettings?: SubscriptionSettings;
}

export interface CreateInvestorSubscriptionsRequest {
  subscriptions: CreateInvestorSubscriptionItem[];
}

/**
 * Response from POST /v3/investors/{userId}/subscriptions
 */
export interface CreateInvestorSubscriptionsResponse {
  message: string;
  results: CreateInvestorSubscriptionResult[];
}

/**
 * Request body for PUT /v3/providers/{providerId}/subscriptions/{id}
 * and PUT /v3/investors/{userId}/subscriptions/{id}
 */
export interface UpdateSubscriptionRequest {
  subscriptionSettings: SubscriptionSettings;
}

/**
 * Response from PUT /v3/.../subscriptions/{id}
 */
export interface UpdateSubscriptionResponse {
  message: string;
  data?: Subscription;
  subscription?: Subscription;
}

/**
 * Response from DELETE /v3/.../subscriptions/{id}
 */
export interface DeleteSubscriptionResponse {
  message: string;
  action?: 'deleted' | 'downgraded';
  strategyCount?: number;
}

/**
 * Paginated v3 subscription list response.
 */
export interface PaginatedSubscriptionsResponse {
  currentPage: number;
  itemsOnPage: number;
  totalPages: number;
  totalItems: number;
  items: SubscriptionWithRelations[];
}

/**
 * Response from POST /v3/providers/{providerId}/subscriptions/backfill
 */
export interface BackfillSubscriptionsResponse {
  message: string;
  totalProcessed: number;
  updated: number;
  skipped: number;
}

// ============================================================
// Helper Functions
// ============================================================

/**
 * Determines the access summary label for subscription settings.
 * Used in admin UI to display "All", "Specific (N)", or "None".
 */
export function getAccessSummary(ids: number[]): string {
  if (ids.length === 0) {
    return 'All';
  }
  if (ids.length === 1 && ids[0] === -1) {
    return 'None';
  }
  return `Specific (${ids.length})`;
}

/**
 * Investor-facing access summary for read-only subscription displays.
 */
export function getInvestorAccessSummary(ids: number[]): string {
  if (ids.length === 0) {
    return 'Full access';
  }
  if (ids.length === 1 && ids[0] === -1) {
    return 'Public only';
  }
  return `Limited access (${ids.length})`;
}

/**
 * Checks if the given settings grant full subscriber access.
 */
export function hasFullAccess(ids: number[]): boolean {
  return ids.length === 0;
}

/**
 * Checks if the given settings revoke all subscriber access.
 */
export function hasNoAccess(ids: number[]): boolean {
  return ids.length === 1 && ids[0] === -1;
}

/**
 * Checks if the given settings grant access to specific items only.
 */
export function hasSpecificAccess(ids: number[]): boolean {
  return ids.length > 0 && !hasNoAccess(ids);
}
